import React from "react";
import { Wallet, Gauge, AlertTriangle, CheckCircle2 } from "lucide-react";

const fmt = (n) =>
  Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/* ──────────────────────────────────────────────────────────
 * Refundable Security Deposit card
 * Render only when amount > 0. Two variants:
 *   - "light"  (checkout, before payment)
 *   - "dark"   (confirmation, after booking)
 * ──────────────────────────────────────────────────────── */
export function DepositCard({ amount, variant = "light" }) {
  if (!amount || Number(amount) <= 0) return null;

  if (variant === "dark") {
    return (
      <div
        data-testid="deposit-card"
        className="rounded-2xl p-5 mt-4 text-white shadow-md"
        style={{ background: "#0a3d80" }}
      >
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(255,255,255,0.18)" }}>
            <Wallet size={20} />
          </div>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80 mb-0.5">
              Refundable Security Deposit
            </p>
            <p className="text-2xl font-extrabold leading-none">
              ${fmt(amount)} <span className="text-sm font-semibold opacity-80">USD</span>
            </p>
            <p className="text-xs mt-2 leading-snug" style={{ color: "rgba(255,255,255,0.85)" }}>
              Collected at pickup. Refunded at drop-off if no damages or extras are owed.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="deposit-card"
      className="rounded-2xl p-5 mt-1"
      style={{ background: "#eaf3ff", border: "1px solid #0a84ff" }}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#0a3d80", color: "#fff" }}>
          <Wallet size={18} />
        </div>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold mb-0.5" style={{ color: "#0a3d80" }}>
            Security Deposit
          </p>
          <p className="text-lg font-extrabold leading-none" style={{ color: "#0a3d80" }}>
            ${fmt(amount)} <span className="text-xs font-semibold">refundable</span>
          </p>
          <p className="text-xs mt-1.5" style={{ color: "#0a3d80", opacity: 0.85 }}>
            Collected at pickup, refunded at drop-off if no damages or extras are owed.{" "}
            <strong>Not added to the total below.</strong>
          </p>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────
 * Mileage Policy card
 * Inputs:
 *   - car: { unlimited_mileage?, mileage_limit? }   (car-level overrides location)
 *   - policy (location): { unlimited_mileage?, mileage_limit_per_day?, extra_mileage_charge? }
 *   - days: trip duration (used to compute total km allowance)
 * ──────────────────────────────────────────────────────── */
export function MileagePolicyCard({ car, policy, days = 1 }) {
  // Car-level explicit setting wins. If the car has a numeric mileage_limit,
  // we treat it as authoritative even when unlimited_mileage is undefined.
  const carHasLimit =
    car &&
    (car.unlimited_mileage === false ||
      (car.mileage_limit != null && Number(car.mileage_limit) > 0));

  let unlimited;
  let perDay;
  let rate;

  if (carHasLimit) {
    unlimited = false;
    perDay = Number(car.mileage_limit || policy?.mileage_limit_per_day || 0);
    rate = Number(policy?.extra_mileage_charge || 0);
  } else if (car && car.unlimited_mileage === true) {
    unlimited = true;
  } else if (policy) {
    unlimited = policy.unlimited_mileage === true;
    perDay = Number(policy.mileage_limit_per_day || 0);
    rate = Number(policy.extra_mileage_charge || 0);
  } else {
    return null;
  }

  if (unlimited) {
    return (
      <div
        data-testid="mileage-policy-card"
        className="rounded-2xl p-4 mt-3 flex items-start gap-3"
        style={{ background: "#e6f9ed", border: "1px solid #b6e5c2" }}
      >
        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#0a5d2b", color: "#fff" }}>
          <CheckCircle2 size={16} />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: "#0a5d2b" }}>
            Mileage Policy
          </p>
          <p className="font-extrabold text-base" style={{ color: "#0a5d2b" }}>
            Unlimited Mileage
          </p>
          <p className="text-xs mt-0.5" style={{ color: "#0a5d2b", opacity: 0.8 }}>
            Drive as far as you'd like — no extra charges.
          </p>
        </div>
      </div>
    );
  }

  if (!perDay || perDay <= 0) return null; // no useful policy info
  const totalKm = perDay * Math.max(1, days);
  return (
    <div
      data-testid="mileage-policy-card"
      className="rounded-2xl p-4 mt-3 flex items-start gap-3"
      style={{ background: "#fff5e6", border: "1px solid #f4cf99" }}
    >
      <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#a05a00", color: "#fff" }}>
        <Gauge size={16} />
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: "#a05a00" }}>
          Mileage Policy
        </p>
        <p className="font-extrabold text-base" style={{ color: "#a05a00" }}>
          {perDay} km/day
          {days > 1 ? ` · ${totalKm} km total for this trip` : ""}
        </p>
        {rate > 0 ? (
          <p className="text-xs mt-0.5" style={{ color: "#a05a00", opacity: 0.85 }}>
            Extra mileage: <strong>${rate.toFixed(2)}/km</strong> over the allowance.
          </p>
        ) : (
          <p className="text-xs mt-0.5" style={{ color: "#a05a00", opacity: 0.85 }}>
            Excess kilometres are billed at drop-off.
          </p>
        )}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────
 * Extra-mileage charge line (confirmation / my-bookings)
 * Only renders if extra_mileage_fee > 0.
 * ──────────────────────────────────────────────────────── */
export function ExtraMileageWarning({ km, rate, fee }) {
  if (!fee || Number(fee) <= 0) return null;
  return (
    <div
      data-testid="extra-mileage-warning"
      className="rounded-lg px-3 py-2 mt-2 flex items-center gap-2 text-xs"
      style={{ background: "#fff5e6", color: "#a05a00", border: "1px solid #f4cf99" }}
    >
      <AlertTriangle size={13} className="shrink-0" />
      <span>
        Extra mileage charged: <strong>${fmt(fee)}</strong>
        {km && rate ? ` (${km} km × $${Number(rate).toFixed(2)})` : ""}
      </span>
    </div>
  );
}
